/**
 * Allys Property Finder
 * 
 * Theme-specific wrapper for the core PropertyFinder component.
 * Customizes the appearance with Allys-specific styling.
 */

'use client';

import { PropertyFinder as CorePropertyFinder } from '@/components/properties';
import { LeafletMap } from '@/components/maps';
import type { Property } from '@/lib/api/clients/properties';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import type { PropertyCardProps } from '@routeroyal/theme-core';
import React from 'react';

interface AllysPropertyFinderProps {
  properties: PropertyCardProps['property'][];
  organisationId: string;
  locale?: string;
  layout?: 'split' | 'side-by-side' | 'list-only';
  emptyMessage?: string;
}

/**
 * Map theme property format to API property format
 */
function mapThemePropertyToApiProperty(themeProperty: PropertyCardProps['property']): Property {
  const priceMatch = themeProperty.price.match(/[\d,.]+/);
  const priceValue = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : 0;

  return {
    id: themeProperty.id,
    name: themeProperty.name,
    description: themeProperty.address,
    status: themeProperty.status === 'sold' ? 'ARCHIVED' : 
            themeProperty.status === 'reserved' ? 'INACTIVE' : 'ACTIVE',
    organisationId: 'default-org',
    locationId: 'default-location',
    propertyTypeId: 'default-type',
    managedAssetId: 'default-asset',
    reference: null,
    currentOwnerUserId: null,
    primaryAgentId: null,
    bedrooms: themeProperty.bedrooms ?? null,
    bathrooms: themeProperty.bathrooms ?? null,
    loungeRoomCount: null,
    toiletCount: null,
    garageCount: null,
    carportCount: null,
    openParkingCount: null,
    parkingSpaces: null,
    storeys: null,
    floorNumber: null,
    totalFloorArea: themeProperty.area ? parseFloat(themeProperty.area) : null,
    totalFloorAreaUnit: 'SQM',
    landArea: null,
    landAreaUnit: null,
    yearBuilt: null,
    yearRenovated: null,
    condition: null,
    aspect: null,
    viewType: null,
    titleType: null,
    zoningCategory: null,
    conservationArea: false,
    primaryCertificationRating: null,
    epcExpiryDate: null,
    currency: DEFAULT_CURRENCY,
    heroMedia: themeProperty.image ? {
      id: themeProperty.id + '-hero',
      url: themeProperty.image,
      thumbnailUrl: themeProperty.image,
      publicId: themeProperty.id + '-hero-public',
      filename: 'hero.jpg',
      contentType: 'image/jpeg',
      size: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } : null,
    primaryImageUrl: themeProperty.image,
    mediaLinks: [],
    attributeValues: {
      askingPrice: {
        attributeId: 'asking-price',
        attributeCode: 'ASKING_PRICE',
        attributeName: 'Asking Price',
        valueType: 'NUMBER',
        valueNumber: priceValue,
        valueString: themeProperty.price,
      },
    },
    createdById: 'system',
    updatedById: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as Property;
}

/**
 * Custom Allys Map Renderer with OpenStreetMap
 * 
 * Uses Leaflet with OpenStreetMap tiles (free and open source)
 */
function AllysMapRenderer({ 
  properties, 
  selectedPropertyId, 
  onMarkerClick 
}: {
  properties: Property[];
  selectedPropertyId: string | null;
  onMarkerClick: (property: Property) => void;
  className?: string;
}) {
  return (
    <div className="w-full h-full relative rounded-r-2xl overflow-hidden">
      <LeafletMap
        properties={properties}
        selectedPropertyId={selectedPropertyId}
        onMarkerClick={onMarkerClick}
      />
    </div>
  );
}

/**
 * Allys Property Finder Component
 * 
 * Wraps CorePropertyFinder with Allys theme styling and custom map renderer
 */
export function AllysPropertyFinder({ 
  properties, 
  organisationId,
  locale = 'en',
  layout = 'split',
  emptyMessage 
}: AllysPropertyFinderProps) {
  // Map theme properties to API properties
  const apiProperties = properties.map(mapThemePropertyToApiProperty);

  return (
    <div className="property-finder-allys">
      <CorePropertyFinder
        properties={apiProperties}
        organisationId={organisationId}
        locale={locale}
        layout={layout}
        {...(emptyMessage ? { emptyMessage } : {})}
        renderMap={AllysMapRenderer}
        className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg"
      />
    </div>
  );
}

